import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, Wind } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiError } from "@/lib/api";
import AnchorMark from "@/components/AnchorMark";
import { CoachingPayment } from "@/components/Pricing";
import Navbar from "@/components/sections/Navbar";
import Footer from "@/components/sections/Footer";

// Each page of the agreement, in order.
const pages = [
  {
    n: "Opening",
    title: "This is not a contract in the traditional sense.",
    body: (
      <>
        <p className="text-lg font-light leading-relaxed text-[#5A5D63]">
          This is a container.
        </p>
        <p className="text-lg font-light leading-relaxed text-[#5A5D63]">
          A space where you begin to:
        </p>
        <ul className="space-y-3 mt-2">
          {[
            "notice your state",
            "return to stillness",
            "meet life with clarity instead of reaction",
          ].map((i) => (
            <li
              key={i}
              className="flex items-baseline gap-3 text-base font-light text-[#15243D]"
            >
              <span className="text-[#C07865] text-xs">—</span>
              {i}
            </li>
          ))}
        </ul>
        <p
          className="serif italic text-2xl md:text-3xl text-[#C07865] leading-snug pt-6"
          style={{ fontWeight: 300 }}
        >
          Strength begins in stillness.
        </p>
      </>
    ),
  },
  {
    n: "The Work",
    title: "This is not about fixing you.",
    body: (
      <>
        <p className="text-lg font-light leading-relaxed text-[#5A5D63]">
          This is about remembering who you are beneath the noise.
        </p>
        <p className="text-base font-light text-[#15243D] pt-2">
          Through this process, you will practice:
        </p>
        <ul className="space-y-3">
          {["awareness", "pause", "presence", "intentional action"].map((i) => (
            <li
              key={i}
              className="flex items-baseline gap-3 text-base font-light text-[#15243D]"
            >
              <span className="text-[#C07865] text-xs">—</span>
              {i}
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    n: "The Commitment",
    title: "This work is simple — but not easy.",
    body: (
      <>
        <p className="text-base font-light text-[#15243D]">Your commitment:</p>
        <ul className="space-y-3">
          {[
            "Show up fully",
            "Practice daily awareness",
            "Notice when you drift",
            "Return, again and again",
          ].map((i) => (
            <li
              key={i}
              className="flex items-baseline gap-3 text-base font-light text-[#15243D]"
            >
              <span className="text-[#C07865] text-xs">—</span>
              {i}
            </li>
          ))}
        </ul>
        <p className="serif italic text-xl text-[#15243D] pt-6" style={{ fontWeight: 300 }}>
          This works to the degree that you participate.
        </p>
      </>
    ),
  },
  {
    n: "The Structure",
    title: "Sessions are a place to slow down and see clearly.",
    isStructure: true,
  },
  {
    n: "The Container",
    title: "These boundaries allow depth.",
    body: (
      <ul className="space-y-3">
        {[
          "Sessions begin and end on time",
          "24-hour notice for rescheduling",
          "Missed sessions may be forfeited",
        ].map((i) => (
          <li
            key={i}
            className="flex items-baseline gap-3 text-base font-light text-[#15243D]"
          >
            <span className="text-[#C07865] text-xs">—</span>
            {i}
          </li>
        ))}
      </ul>
    ),
  },
  {
    n: "Responsibility",
    title: "You are not here to be carried.",
    body: (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <div>
          <p className="overline mb-4">Coach</p>
          <ul className="space-y-3">
            {["Hold grounded space", "Guide awareness", "Reflect truth"].map(
              (i) => (
                <li
                  key={i}
                  className="flex items-baseline gap-3 text-base font-light text-[#15243D]"
                >
                  <span className="text-[#C07865] text-xs">—</span>
                  {i}
                </li>
              )
            )}
          </ul>
        </div>
        <div>
          <p className="overline mb-4">Client</p>
          <ul className="space-y-3">
            {["Be honest", "Practice the work", "Take ownership"].map((i) => (
              <li
                key={i}
                className="flex items-baseline gap-3 text-base font-light text-[#15243D]"
              >
                <span className="text-[#C07865] text-xs">—</span>
                {i}
              </li>
            ))}
          </ul>
        </div>
      </div>
    ),
  },
  {
    n: "The Truth",
    title: null,
    body: (
      <div className="space-y-5">
        <p
          className="serif text-3xl md:text-4xl text-[#15243D] leading-snug"
          style={{ fontWeight: 300 }}
        >
          You are not your thoughts.
        </p>
        <p
          className="serif text-3xl md:text-4xl text-[#15243D] leading-snug"
          style={{ fontWeight: 300 }}
        >
          You are not your reactions.
        </p>
        <p
          className="serif italic text-3xl md:text-4xl text-[#C07865] leading-snug pt-4"
          style={{ fontWeight: 300 }}
        >
          You are the awareness beneath them.
        </p>
      </div>
    ),
  },
  {
    n: "Outcome",
    title: "You may experience:",
    body: (
      <>
        <ul className="space-y-3">
          {[
            "Less reactivity",
            "More clarity",
            "Faster recovery from stress",
            "Deeper presence",
          ].map((i) => (
            <li
              key={i}
              className="flex items-baseline gap-3 text-base font-light text-[#15243D]"
            >
              <span className="text-[#C07865] text-xs">—</span>
              {i}
            </li>
          ))}
        </ul>
        <div className="pt-6 space-y-2">
          <p className="text-base font-light text-[#5A5D63]">
            The problems may not disappear.
          </p>
          <p
            className="serif italic text-xl text-[#15243D]"
            style={{ fontWeight: 300 }}
          >
            But your relationship to them will change.
          </p>
        </div>
      </>
    ),
  },
  {
    n: "Investment",
    title: "Program Investment & Payment Terms.",
    isInvestment: true,
  },
  {
    n: "Confidentiality",
    title: null,
    body: (
      <p
        className="serif text-2xl md:text-3xl text-[#15243D] leading-snug"
        style={{ fontWeight: 300 }}
      >
        Everything shared remains <em className="italic text-[#C07865]">confidential</em>.
      </p>
    ),
  },
];

// Reusable "blank line" — the agreement uses ____ placeholders confirmed in onboarding.
function Blank({ label }) {
  return (
    <div className="flex flex-col gap-1 py-3 border-b border-[#15243D]/15">
      <span className="text-[10px] tracking-[0.22em] uppercase text-[#8A8D93]">
        {label}
      </span>
      <span className="serif italic text-[#8A8D93] text-base" style={{ fontWeight: 300 }}>
        — to be confirmed in onboarding —
      </span>
    </div>
  );
}

export default function AgreementPage() {
  const today = useMemo(() => {
    const d = new Date();
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, []);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    signature: "",
    signed_date: today,
    commit_see_clearly: false,
    commit_pause: false,
    commit_return: false,
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const allChecked =
    form.commit_see_clearly && form.commit_pause && form.commit_return;

  const submit = async (e) => {
    e.preventDefault();
    if (!allChecked) {
      toast.error("Please accept all three commitments.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/agreement", form);
      setSubmitted(true);
      toast.success("Agreement signed.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="agreement-page"
      className="min-h-screen"
      style={{ backgroundColor: "#F7F5F0" }}
    >
      <Navbar />

      <main className="pt-32 md:pt-40 pb-24 md:pb-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link
            to="/"
            data-testid="agreement-back-link"
            className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#5A5D63] hover:text-[#C07865] transition-colors mb-12"
          >
            <ArrowLeft size={14} strokeWidth={1.5} />
            Return home
          </Link>

          {!submitted ? (
            <>
              {/* Cover */}
              <header className="text-center mb-24 md:mb-32" data-testid="agreement-cover">
                <div className="flex justify-center mb-10">
                  <AnchorMark size={90} color="#15243D" />
                </div>
                <p
                  className="serif text-lg tracking-[0.4em] text-[#15243D] mb-6"
                  style={{ fontWeight: 400 }}
                >
                  ANCHOR
                </p>
                <h1
                  className="serif text-5xl md:text-7xl text-[#15243D] leading-[1.05] tracking-tight mb-6"
                  style={{ fontWeight: 300 }}
                >
                  The <em className="italic text-[#C07865]">Agreement.</em>
                </h1>
                <p
                  className="serif italic text-lg md:text-xl text-[#5A5D63] max-w-md mx-auto"
                  style={{ fontWeight: 300 }}
                >
                  The container for returning to yourself.
                </p>
              </header>

              {/* Pages */}
              <article className="space-y-24 md:space-y-32" data-testid="agreement-pages">
                {pages.map((p, i) => (
                  <section
                    key={p.n}
                    data-testid={`agreement-page-${i}`}
                    className="relative"
                  >
                    <div className="flex items-baseline justify-between mb-8">
                      <p className="overline">{p.n}</p>
                      <span
                        className="serif text-sm tracking-[0.2em] text-[#8A8D93]"
                        style={{ fontWeight: 300 }}
                      >
                        {String(i + 1).padStart(2, "0")} / {String(pages.length).padStart(2, "0")}
                      </span>
                    </div>

                    {p.title && (
                      <h2
                        className="serif text-3xl md:text-4xl text-[#15243D] leading-[1.15] mb-8 max-w-2xl"
                        style={{ fontWeight: 300 }}
                      >
                        {p.title}
                      </h2>
                    )}

                    {p.isStructure ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2">
                        <Blank label="Program length" />
                        <Blank label="Sessions" />
                        <Blank label="Format" />
                        <Blank label="Access between sessions" />
                      </div>
                    ) : p.isInvestment ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2">
                        <Blank label="Program investment" />
                        <Blank label="Payment terms" />
                      </div>
                    ) : (
                      <div className="space-y-6">{p.body}</div>
                    )}

                    {/* Ornamental divider between sections */}
                    <div className="flex items-center justify-center mt-20">
                      <span className="w-12 h-px bg-[#15243D]/15" />
                      <AnchorMark size={20} color="#15243D" className="mx-4 opacity-30" />
                      <span className="w-12 h-px bg-[#15243D]/15" />
                    </div>
                  </section>
                ))}
              </article>

              {/* Final page — breath + signing */}
              <section className="mt-12" data-testid="agreement-final">
                <div className="text-center mb-16">
                  <p className="overline mb-8">The Agreement</p>
                  <div className="inline-flex items-center gap-3 text-xs tracking-[0.22em] uppercase text-[#5A5D63] mb-10">
                    <Wind size={14} strokeWidth={1.25} className="text-[#C07865]" />
                    Take one breath. Notice your state.
                  </div>
                </div>

                <form
                  onSubmit={submit}
                  className="space-y-12"
                  data-testid="agreement-form"
                >
                  {/* Three commitments */}
                  <div className="space-y-6">
                    {[
                      ["commit_see_clearly", "I am willing to see myself clearly."],
                      ["commit_pause", "I am willing to pause instead of react."],
                      ["commit_return", "I am willing to return to myself — again and again."],
                    ].map(([key, label]) => (
                      <label
                        key={key}
                        className="flex items-start gap-4 cursor-pointer group border-t border-[#15243D]/10 pt-6"
                      >
                        <Checkbox
                          checked={form[key]}
                          onCheckedChange={(v) => set(key, v === true)}
                          data-testid={`agreement-commit-${key}`}
                          className="mt-1.5 rounded-none border-[#15243D]/30 data-[state=checked]:bg-[#C07865] data-[state=checked]:border-[#C07865]"
                        />
                        <span
                          className="serif text-xl md:text-2xl text-[#15243D] leading-snug group-hover:text-[#C07865] transition-colors"
                          style={{ fontWeight: 300 }}
                        >
                          {label}
                        </span>
                      </label>
                    ))}
                  </div>

                  {/* Signing */}
                  <div className="pt-10 border-t border-[#15243D]/10 space-y-8">
                    <p className="overline">Signature</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <label className="text-xs tracking-[0.18em] uppercase text-[#15243D] mb-2 block">
                          Name
                        </label>
                        <input
                          required
                          value={form.full_name}
                          onChange={(e) => set("full_name", e.target.value)}
                          className="anchor-input"
                          data-testid="agreement-name-input"
                        />
                      </div>
                      <div>
                        <label className="text-xs tracking-[0.18em] uppercase text-[#15243D] mb-2 block">
                          Email
                        </label>
                        <input
                          type="email"
                          required
                          value={form.email}
                          onChange={(e) => set("email", e.target.value)}
                          className="anchor-input"
                          data-testid="agreement-email-input"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-xs tracking-[0.18em] uppercase text-[#15243D] mb-2 block">
                        Signature
                      </label>
                      <input
                        required
                        value={form.signature}
                        onChange={(e) => set("signature", e.target.value)}
                        placeholder="Type your full name to sign"
                        className="anchor-input serif italic"
                        style={{ fontSize: "1.35rem" }}
                        data-testid="agreement-signature-input"
                      />
                    </div>

                    <div>
                      <label className="text-xs tracking-[0.18em] uppercase text-[#15243D] mb-2 block">
                        Date
                      </label>
                      <input
                        value={form.signed_date}
                        onChange={(e) => set("signed_date", e.target.value)}
                        className="anchor-input"
                        data-testid="agreement-date-input"
                      />
                    </div>

                    <div>
                      <label className="text-xs tracking-[0.18em] uppercase text-[#15243D] mb-2 block">
                        Anything you want me to know{" "}
                        <span className="text-[#8A8D93] normal-case tracking-normal">
                          (optional)
                        </span>
                      </label>
                      <textarea
                        rows={3}
                        value={form.notes}
                        onChange={(e) => set("notes", e.target.value)}
                        className="anchor-input"
                        data-testid="agreement-notes-input"
                      />
                    </div>
                  </div>

                  <div className="pt-10 border-t border-[#15243D]/10">
                    <button
                      type="submit"
                      disabled={loading || !allChecked}
                      className="btn-primary"
                      data-testid="agreement-submit-button"
                    >
                      {loading ? "Signing…" : "Sign agreement"}
                      <ArrowUpRight size={16} strokeWidth={1.5} />
                    </button>
                    {!allChecked && (
                      <p className="mt-6 text-xs text-[#8A8D93]">
                        Please accept all three commitments to continue.
                      </p>
                    )}
                  </div>
                </form>
              </section>
            </>
          ) : (
            <div data-testid="agreement-success" className="max-w-2xl">
              <div className="flex mb-12">
                <AnchorMark size={70} color="#15243D" />
              </div>
              <p className="overline mb-6">Signed</p>
              <h1
                className="serif text-5xl md:text-6xl text-[#15243D] leading-[1.05] mb-8"
                style={{ fontWeight: 300 }}
              >
                Thank you,<br />
                <em className="italic text-[#C07865]">{form.full_name}</em>.
              </h1>
              <p className="text-lg font-light leading-relaxed text-[#5A5D63] mb-3">
                Your signed agreement has been received on{" "}
                <span className="text-[#15243D]">{form.signed_date}</span>.
              </p>
              <p className="text-base font-light leading-relaxed text-[#5A5D63] mb-8">
                A copy will be sent to{" "}
                <span className="text-[#15243D]">{form.email}</span>. We’ll
                confirm the structure, sessions, and investment in your
                onboarding conversation.
              </p>

              <CoachingPayment />

              <Link
                to="/"
                className="btn-secondary"
                data-testid="agreement-home-button"
              >
                Return home
              </Link>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
